import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { translate } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { lastValueFrom } from 'rxjs';

import { EventRequestConfig, EventType, OrgDataSchema } from '@pwp-common';

import { EventRequestConfigService } from '../../../../services/event/event-requests/event-request-config.service';
import { OrgDataService } from '../../../../services/orgs/org-data/org-data.service';
import { makeAuditEntry } from '../../../generic/settings/common/audit-entry/helpers/make-audit-entry';
import { AuditEntry } from '../../../generic/settings/common/audit-entry/interfaces';
import { SettingsModule } from '../../../generic/settings/settings.module';
import { editorOutputToDb } from '../editor/editor-output/edit-output-to-db/editor-output-to-db';
import { EventRequestConfigEditorComponent } from '../editor/event-request-config-editor/event-request-config-editor.component';
import { EventRequestConfigEditorOutput } from '../editor/interfaces';
import { EventRequestConfigTableRow } from '../editor-table/event-request-config-table-row/event-request-config-table-row';

import { EventRequestConfigEditorDialogData } from './interfaces';

@Component({
  selector: 'app-event-request-config-editor-dialog',
  standalone: true,
  imports: [EventRequestConfigEditorComponent, ReactiveFormsModule, SettingsModule, ToastModule],
  templateUrl: './event-request-config-editor-dialog.component.html',
  styleUrls: ['./event-request-config-editor-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class EventRequestConfigEditorDialogComponent {
  ///////////////////////////////////////////////////////////////////////
  // Dependencies
  ///////////////////////////////////////////////////////////////////////

  private readonly dialogConfig = inject(DynamicDialogConfig);

  private readonly dialogRef = inject(DynamicDialogRef);

  private readonly orgDataService = inject(OrgDataService);

  private readonly eventRequestConfigService = inject(EventRequestConfigService);

  private readonly messageService = inject(MessageService);

  ///////////////////////////////////////////////////////////////////////
  // Variables
  ///////////////////////////////////////////////////////////////////////

  private readonly dialogData = this.dialogConfig.data as EventRequestConfigEditorDialogData;

  private readonly orgData = this.dialogData.orgData;

  public row = this.dialogData.row;

  public auditEntry: AuditEntry | undefined = makeAuditEntry(this.row.data.eventRequestConfigData);

  public readonly configControl = new UntypedFormControl(this.row.editorOutput);

  public readonly existingDisplayNames = this.getOtherEventTypeNames();

  ///////////////////////////////////////////////////////////////////////
  // Settings Hooks
  ///////////////////////////////////////////////////////////////////////

  public isValid = (): boolean => this.configControl.valid;

  public resetForm = (): void => {
    this.configControl.reset(this.row.editorOutput);
  };

  public upload = async (): Promise<void> => {
    const editorOutput = this.configControl.value as EventRequestConfigEditorOutput;
    const displayName = `${editorOutput.displayName ?? ''}`.trim();

    const requestedUpdate = editorOutputToDb({
      eventRequestConfig: this.row.data.eventRequestConfigData,
      editorOutput,
      eventRequestConfigId: this.row.data.eventType.getInternalName(),
    });

    if (this.row.data.eventRequestConfigData) {
      const updateSucceeded = await this.eventRequestConfigService.uploadIfNotEditingStaleObject(requestedUpdate);
      if (!updateSucceeded) {
        this.showContentionWarning();
        return;
      }
    } else {
      await lastValueFrom(
        this.eventRequestConfigService.upload({ obj: requestedUpdate, mode: 'create', generateId: false }),
      );
    }

    await this.saveDisplayNameIfChanged(displayName);

    this.applySavedState(displayName, requestedUpdate);
    this.dialogRef.close(true);
  };

  ///////////////////////////////////////////////////////////////////////
  // Save Helpers
  ///////////////////////////////////////////////////////////////////////

  private getOtherEventTypeNames(): string[] {
    return this.orgData
      .getEventTypes()
      .filter((eventType) => eventType.getId() !== this.row.data.eventType.getId())
      .map((eventType) => eventType.getDisplayName());
  }

  private async saveDisplayNameIfChanged(displayName: string): Promise<void> {
    if (displayName === this.row.data.eventType.getDisplayName()) {
      return;
    }

    const eventTypes = this.getEventTypesToSave(displayName);
    await this.orgDataService.updateDocFields({
      docId: this.orgData.getId(),
      data: { [OrgDataSchema.eventTypes]: eventTypes.map((eventType) => eventType.serialize()) },
    });
  }

  private getEventTypesToSave(displayName: string): EventType[] {
    const updatedEventType = new EventType({
      internalName: this.row.data.eventType.getId(),
      displayName,
    });

    return this.orgData
      .getEventTypes()
      .map((eventType) => (eventType.getId() === updatedEventType.getId() ? updatedEventType : eventType));
  }

  private applySavedState(displayName: string, savedConfig: EventRequestConfig): void {
    const updatedEventType = new EventType({
      internalName: this.row.data.eventType.getId(),
      displayName,
    });

    this.row = new EventRequestConfigTableRow({
      eventType: updatedEventType,
      eventRequestConfigData: savedConfig,
    });
    this.auditEntry = makeAuditEntry(savedConfig);
    this.resetForm();
  }

  private showContentionWarning(): void {
    this.messageService.add({
      severity: 'warn',
      summary: translate('settings.uploadFailedDueToContentionTitle'),
      detail: translate('settings.uploadFailedDueToContentionBody'),
    });
  }
}
